/**
 * ExecutionDetailDrawer
 * Replaces the old cramped dialog with a right-side drawer that shows the
 * execution summary, stop reason, step-by-step trace, and full message log.
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Drawer,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Chat as ChatIcon,
  FactCheck as ValidationIcon,
  WorkspacePremium as SkillIcon,
} from '@mui/icons-material';

import { agentApi } from '../../../services/api';
import type { AgentValidationRun } from '../../../types';
import { LoadingSpinner, UsageBreakdownPanel } from '../../common';
import { formatModelPricing, getModelDisplayName } from '../../../utils/modelPricing';

interface ExecutionDetailDrawerProps {
  executionId: string | null;
  open: boolean;
  onClose: () => void;
}

const STATUS_COLORS: Record<string, 'success' | 'error' | 'warning' | 'default'> = {
  COMPLETED: 'success',
  ERROR: 'error',
  RUNNING: 'warning',
  CANCELLED: 'default',
};

const VALIDATION_STATUS_COLORS: Record<string, 'success' | 'error' | 'warning' | 'default'> = {
  PASSED: 'success',
  FAILED: 'error',
  ACTIONED: 'warning',
  ERROR: 'error',
};

const SEVERITY_COLORS: Record<string, 'success' | 'error' | 'warning' | 'default'> = {
  low: 'default',
  medium: 'success',
  high: 'warning',
  critical: 'error',
};

const formatValidationLabel = (value?: string | null): string =>
  value ? value.replace(/_/g, ' ') : 'none';

export const ExecutionDetailDrawer: React.FC<ExecutionDetailDrawerProps> = ({
  executionId,
  open,
  onClose,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
  const [drilldownExecutionId, setDrilldownExecutionId] = React.useState<string | null>(null);
  const activeExecutionId = drilldownExecutionId || executionId;

  React.useEffect(() => {
    setDrilldownExecutionId(null);
  }, [executionId, open]);

  const { data: detail, isLoading: detailLoading } = useQuery({
    queryKey: ['execution-detail', activeExecutionId],
    queryFn: () => (activeExecutionId ? agentApi.getExecution(activeExecutionId) : null),
    enabled: !!activeExecutionId && open,
  });

  const { data: messages, isLoading: messagesLoading } = useQuery({
    queryKey: ['execution-messages', activeExecutionId],
    queryFn: () => (activeExecutionId ? agentApi.getExecutionMessages(activeExecutionId) : []),
    enabled: !!activeExecutionId && open,
  });

  const loading = detailLoading || messagesLoading;
  const validationRuns = (detail?.validation_runs || []) as AgentValidationRun[];
  const validationFailed = validationRuns.filter(
    (run) => run.status === 'FAILED' || run.status === 'ACTIONED' || run.status === 'ERROR'
  ).length;
  const hasSkillAudit = Boolean(
    detail?.metadata && Object.prototype.hasOwnProperty.call(detail.metadata, 'skills')
  );
  const skillAudit = (detail?.metadata?.skills || {}) as {
    catalog_count?: number;
    active?: Array<{
      skill_id: number;
      name: string;
      slug: string;
      version: number;
      activation_source: string;
      missing_required_tools?: string[];
    }>;
    missing_requested?: { ids?: number[]; slugs?: string[] };
  };

  return (
    <Drawer
      anchor={isSmDown ? 'bottom' : 'right'}
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 640, md: 760 },
          maxHeight: { xs: '92vh', sm: '100%' },
        },
      }}
    >
      <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" fontWeight={700}>
              Execution details
            </Typography>
            {activeExecutionId ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontFamily: 'monospace' }}
              >
                {activeExecutionId}
              </Typography>
            ) : null}
          </Box>
          <IconButton onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Stack>

        <Box sx={{ flex: 1, overflowY: 'auto', pr: 0.5 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <LoadingSpinner />
            </Box>
          ) : !detail ? (
            <Alert severity="info">No data for this execution.</Alert>
          ) : (
            <Stack spacing={2}>
              <Paper
                variant="outlined"
                sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.04) }}
              >
                <Grid container spacing={1.5}>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Typography variant="caption" color="text.secondary">Status</Typography>
                    <Box>
                      <Chip
                        size="small"
                        label={detail.status}
                        color={STATUS_COLORS[detail.status] || 'default'}
                      />
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Typography variant="caption" color="text.secondary">Type</Typography>
                    <Box>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={formatValidationLabel(detail.execution_type || 'agent')}
                      />
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Typography variant="caption" color="text.secondary">Duration</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {detail.duration_seconds?.toFixed(2)}s
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Typography variant="caption" color="text.secondary">Tokens</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {detail.total_tokens?.toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Typography variant="caption" color="text.secondary">Cost</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      ${detail.total_cost?.toFixed(6)}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary">Model Snapshot</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {getModelDisplayName(detail, 'No model recorded')}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary">Input / Output Price</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {formatModelPricing(detail)}
                    </Typography>
                  </Grid>
                  {detail.result?.iterations ? (
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Typography variant="caption" color="text.secondary">Iterations</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {detail.result.iterations}
                      </Typography>
                    </Grid>
                  ) : null}
                  {detail.started_at ? (
                    <Grid size={{ xs: 12, sm: 9 }}>
                      <Typography variant="caption" color="text.secondary">Started</Typography>
                      <Typography variant="body2">
                        {new Date(detail.started_at).toLocaleString()}
                      </Typography>
                    </Grid>
                  ) : null}
                  {detail.parent_execution_id ? (
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="caption" color="text.secondary">Parent execution</Typography>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.25 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
                        >
                          {detail.parent_execution_id}
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setDrilldownExecutionId(detail.parent_execution_id || null)}
                        >
                          Open
                        </Button>
                      </Stack>
                    </Grid>
                  ) : null}
                </Grid>
                <UsageBreakdownPanel
                  usage={detail.usage_breakdown}
                  compact
                  sx={{ mt: 2 }}
                />
              </Paper>

              {drilldownExecutionId ? (
                <Alert
                  severity="info"
                  action={
                    <Button size="small" onClick={() => setDrilldownExecutionId(null)}>
                      Back
                    </Button>
                  }
                >
                  Showing linked validator execution.
                </Alert>
              ) : null}

              {hasSkillAudit ? (
                <Box>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <SkillIcon fontSize="small" />
                    <Typography variant="subtitle2" fontWeight={700}>
                      Skills
                    </Typography>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`${skillAudit.active?.length || 0} active`}
                    />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`${skillAudit.catalog_count || 0} assigned`}
                    />
                  </Stack>
                  {(skillAudit.active || []).length === 0 ? (
                    <Alert severity="info">No skill instructions activated for this execution.</Alert>
                  ) : (
                    <Stack spacing={1}>
                      {(skillAudit.active || []).map((skill) => (
                        <Paper key={`${skill.skill_id}-${skill.version}`} variant="outlined" sx={{ p: 1.25 }}>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="flex-start"
                            spacing={1}
                          >
                            <Box>
                              <Typography variant="subtitle2" fontWeight={700}>
                                {skill.name} v{skill.version}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {skill.slug}
                              </Typography>
                            </Box>
                            <Chip
                              size="small"
                              color="primary"
                              variant="outlined"
                              label={formatValidationLabel(skill.activation_source)}
                            />
                          </Stack>
                          {(skill.missing_required_tools || []).length > 0 ? (
                            <Alert severity="warning" sx={{ mt: 1, py: 0.25 }}>
                              Missing required tools: {skill.missing_required_tools?.join(', ')}
                            </Alert>
                          ) : null}
                        </Paper>
                      ))}
                    </Stack>
                  )}
                  {(skillAudit.missing_requested?.ids || []).length > 0 ||
                  (skillAudit.missing_requested?.slugs || []).length > 0 ? (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      Requested skills were unavailable: {[
                        ...(skillAudit.missing_requested?.ids || []).map(String),
                        ...(skillAudit.missing_requested?.slugs || []),
                      ].join(', ')}
                    </Alert>
                  ) : null}
                </Box>
              ) : null}

              <Alert
                severity={
                  detail.status === 'ERROR'
                    ? 'error'
                    : detail.status === 'CANCELLED'
                      ? 'warning'
                      : detail.result?.max_iterations_reached
                        ? 'warning'
                        : detail.status === 'RUNNING'
                          ? 'info'
                          : 'success'
                }
              >
                {detail.status === 'ERROR' && (
                  <>
                    <strong>Error occurred.</strong>{' '}
                    <Box component="span" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {detail.error || 'Unknown error'}
                    </Box>
                  </>
                )}
                {detail.status === 'CANCELLED' && 'Execution cancelled before completion.'}
                {detail.status === 'RUNNING' && 'Execution is still in progress.'}
                {detail.status === 'COMPLETED' && detail.result?.max_iterations_reached && (
                  <>
                    Reached max iterations ({detail.result.iterations}). Task may be partially
                    complete.
                  </>
                )}
                {detail.status === 'COMPLETED' && !detail.result?.max_iterations_reached && (
                  <>Completed normally after {detail.result?.iterations || '—'} iteration(s).</>
                )}
              </Alert>

              <Box>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                  <ValidationIcon fontSize="small" />
                  <Typography variant="subtitle2" fontWeight={700}>
                    Validation runs
                  </Typography>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`${validationRuns.length} total`}
                  />
                  {validationFailed > 0 ? (
                    <Chip
                      size="small"
                      color="warning"
                      variant="outlined"
                      label={`${validationFailed} actioned`}
                    />
                  ) : null}
                </Stack>

                {validationRuns.length === 0 ? (
                  <Alert severity="info">
                    No validation runs were recorded for this execution.
                  </Alert>
                ) : (
                  <Stack spacing={1}>
                    {[...validationRuns]
                      .sort(
                        (a, b) =>
                          new Date(a.created_at).getTime() -
                          new Date(b.created_at).getTime()
                      )
                      .map((run) => (
                        <Paper
                          key={run.id}
                          variant="outlined"
                          sx={{
                            p: 1.25,
                            borderLeft: '3px solid',
                            borderColor:
                              run.status === 'PASSED'
                                ? 'success.main'
                                : run.status === 'ACTIONED'
                                  ? 'warning.main'
                                  : 'error.main',
                          }}
                        >
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="flex-start"
                            spacing={1}
                            flexWrap="wrap"
                            useFlexGap
                          >
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography variant="subtitle2" fontWeight={700}>
                                {run.policy_name || 'Validator'}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mt: 0.25, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                              >
                                {run.message || 'No validation message.'}
                              </Typography>
                            </Box>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                              <Chip
                                size="small"
                                color={VALIDATION_STATUS_COLORS[run.status] || 'default'}
                                label={run.status}
                              />
                              <Chip
                                size="small"
                                variant="outlined"
                                color={SEVERITY_COLORS[String(run.severity || '').toLowerCase()] || 'default'}
                                label={run.severity || 'medium'}
                              />
                            </Stack>
                          </Stack>

                          <Stack
                            direction="row"
                            spacing={0.75}
                            flexWrap="wrap"
                            useFlexGap
                            sx={{ mt: 1 }}
                          >
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`hook: ${formatValidationLabel(run.hook)}`}
                            />
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`action: ${formatValidationLabel(run.action)}`}
                            />
                            {run.target_type || run.target_name ? (
                              <Chip
                                size="small"
                                variant="outlined"
                                label={`target: ${formatValidationLabel(run.target_type)} / ${formatValidationLabel(run.target_name)}`}
                              />
                            ) : null}
                            <Chip
                              size="small"
                              variant="outlined"
                              label={new Date(run.created_at).toLocaleString()}
                            />
                            {run.validator_execution ? (
                              <Chip
                                size="small"
                                color="info"
                                variant="outlined"
                                onClick={() => setDrilldownExecutionId(run.validator_execution)}
                                label={`validator execution: ${run.validator_execution.slice(0, 8)}`}
                              />
                            ) : null}
                          </Stack>

                          {run.details && Object.keys(run.details).length > 0 ? (
                            <Accordion disableGutters elevation={0} sx={{ mt: 1 }}>
                              <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                sx={{
                                  minHeight: 36,
                                  px: 1,
                                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                                  borderRadius: 1,
                                  '& .MuiAccordionSummary-content': { my: 0.5 },
                                }}
                              >
                                <Typography variant="caption" fontWeight={700}>
                                  Findings and verdict
                                </Typography>
                              </AccordionSummary>
                              <AccordionDetails sx={{ p: 1 }}>
                                <Box
                                  component="pre"
                                  sx={{
                                    m: 0,
                                    p: 1,
                                    fontSize: '0.72rem',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    bgcolor: isDark ? 'grey.900' : 'grey.50',
                                    borderRadius: 1,
                                    maxHeight: 260,
                                    overflow: 'auto',
                                  }}
                                >
                                  {JSON.stringify(run.details, null, 2)}
                                </Box>
                              </AccordionDetails>
                            </Accordion>
                          ) : null}
                        </Paper>
                      ))}
                  </Stack>
                )}
              </Box>

              {detail.steps && detail.steps.length > 0 ? (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Steps ({detail.steps.length})
                  </Typography>
                  <Stack spacing={1}>
                    {[...detail.steps]
                      .sort((a: any, b: any) => a.step_number - b.step_number)
                      .map((step: any) => {
                        const isTool = step.step_type === 'TOOL_CALL';
                        const color = isTool
                          ? 'info.main'
                          : step.step_type === 'REASONING'
                            ? 'warning.main'
                            : step.step_type === 'OBSERVATION'
                              ? 'secondary.main'
                              : step.step_type === 'VALIDATION_RETRY'
                                ? 'warning.main'
                              : step.step_type === 'SPEECH_TRANSCRIPTION'
                                ? 'primary.main'
                              : step.step_type === 'SPEECH_SYNTHESIS'
                                ? 'primary.dark'
                              : step.step_type === 'RESPONSE'
                                ? 'success.main'
                                : 'grey.400';
                        const stepLabel = step.step_type === 'SPEECH_TRANSCRIPTION'
                          ? 'Speech transcription'
                          : step.step_type === 'SPEECH_SYNTHESIS'
                            ? 'Speech synthesis'
                          : step.step_type;
                        return (
                          <Paper
                            key={step.id}
                            variant="outlined"
                            sx={{ p: 1.5, borderLeft: '3px solid', borderColor: color }}
                          >
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center"
                              sx={{ mb: 0.75 }}
                              flexWrap="wrap"
                              useFlexGap
                            >
                              <Chip
                                size="small"
                                label={isTool ? step.tool_name || 'Tool call' : stepLabel}
                                color={isTool ? 'info' : 'default'}
                              />
                              <Typography variant="caption" color="text.secondary">
                                Step {step.step_number} · {step.tokens_used} tok · $
                                {step.cost?.toFixed(6) || '0.000000'}
                              </Typography>
                            </Stack>
                            <UsageBreakdownPanel
                              usage={step.usage_breakdown || step.metadata?.usage_breakdown}
                              title="Step usage"
                              compact
                              sx={{ mb: 1 }}
                            />

                            {!isTool && step.content ? (
                              <Box
                                component="pre"
                                sx={{
                                  m: 0,
                                  p: 1,
                                  fontSize: '0.75rem',
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                  bgcolor: isDark ? 'grey.900' : 'grey.50',
                                  borderRadius: 1,
                                  maxHeight: 220,
                                  overflow: 'auto',
                                }}
                              >
                                {step.content}
                              </Box>
                            ) : null}

                            {isTool ? (
                              <Stack spacing={0.75}>
                                <Box>
                                  <Typography variant="caption" color="text.secondary" fontWeight={700}>
                                    Input
                                  </Typography>
                                  <Box
                                    component="pre"
                                    sx={{
                                      m: 0,
                                      p: 1,
                                      fontSize: '0.7rem',
                                      whiteSpace: 'pre-wrap',
                                      wordBreak: 'break-word',
                                      bgcolor: isDark ? 'grey.900' : 'grey.50',
                                      borderRadius: 1,
                                      maxHeight: 180,
                                      overflow: 'auto',
                                    }}
                                  >
                                    {step.tool_input ? JSON.stringify(step.tool_input, null, 2) : 'null'}
                                  </Box>
                                </Box>
                                <Box>
                                  <Typography variant="caption" color="text.secondary" fontWeight={700}>
                                    Output
                                  </Typography>
                                  <Box
                                    component="pre"
                                    sx={{
                                      m: 0,
                                      p: 1,
                                      fontSize: '0.7rem',
                                      whiteSpace: 'pre-wrap',
                                      wordBreak: 'break-word',
                                      bgcolor:
                                        step.tool_output?.success === false
                                          ? isDark
                                            ? 'error.900'
                                            : 'error.50'
                                          : isDark
                                            ? 'success.900'
                                            : 'success.50',
                                      borderRadius: 1,
                                      maxHeight: 180,
                                      overflow: 'auto',
                                    }}
                                  >
                                    {step.tool_output ? JSON.stringify(step.tool_output, null, 2) : 'null'}
                                  </Box>
                                </Box>
                              </Stack>
                            ) : null}

                            {step.messages_sent && step.messages_sent.length > 0 ? (
                              <Accordion disableGutters elevation={0} sx={{ mt: 1 }}>
                                <AccordionSummary
                                  expandIcon={<ExpandMoreIcon />}
                                  sx={{
                                    minHeight: 36,
                                    px: 1,
                                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                                    borderRadius: 1,
                                    '& .MuiAccordionSummary-content': { my: 0.5 },
                                  }}
                                >
                                  <Typography variant="caption" fontWeight={700}>
                                    Context sent to LLM · {step.messages_sent.length} msgs
                                  </Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{ p: 1 }}>
                                  <Stack spacing={0.75}>
                                    {step.messages_sent.map((msg: any, i: number) => (
                                      <Paper
                                        key={i}
                                        variant="outlined"
                                        sx={{
                                          p: 1,
                                          borderLeft: '3px solid',
                                          borderColor:
                                            msg.role === 'system'
                                              ? 'info.main'
                                              : msg.role === 'user'
                                                ? 'primary.main'
                                                : msg.role === 'assistant'
                                                  ? 'success.main'
                                                  : 'grey.400',
                                        }}
                                      >
                                        <Chip
                                          size="small"
                                          label={(msg.role || 'unknown').toUpperCase()}
                                          sx={{ height: 18, fontSize: '0.6rem' }}
                                        />
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            mt: 0.5,
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                            fontSize: '0.72rem',
                                          }}
                                        >
                                          {typeof msg.content === 'string'
                                            ? msg.content
                                            : JSON.stringify(msg.content, null, 2)}
                                        </Typography>
                                      </Paper>
                                    ))}
                                  </Stack>
                                </AccordionDetails>
                              </Accordion>
                            ) : null}
                          </Paper>
                        );
                      })}
                  </Stack>
                </Box>
              ) : null}

              {messages && messages.length > 0 ? (
                <Box>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <ChatIcon fontSize="small" />
                    <Typography variant="subtitle2" fontWeight={700}>
                      Conversation
                    </Typography>
                  </Stack>
                  <Stack spacing={1}>
                    {messages.map((msg: any) => (
                      <Paper
                        key={msg.id}
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          borderLeft: '3px solid',
                          borderColor:
                            msg.role === 'USER'
                              ? 'primary.main'
                              : msg.role === 'ASSISTANT'
                                ? 'success.main'
                                : 'grey.500',
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                          <Chip
                            size="small"
                            label={msg.role}
                            color={
                              msg.role === 'USER'
                                ? 'primary'
                                : msg.role === 'ASSISTANT'
                                  ? 'success'
                                  : 'default'
                            }
                          />
                          <Typography variant="caption" color="text.secondary">
                            {new Date(msg.created_at).toLocaleString()}
                          </Typography>
                        </Stack>
                        <Typography
                          variant="body2"
                          sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                        >
                          {msg.content}
                        </Typography>
                      </Paper>
                    ))}
                  </Stack>
                </Box>
              ) : null}
            </Stack>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default ExecutionDetailDrawer;
